/**
 * Section: Split media and text
 * ------------------------------------------------------------------------------
 * Component file for the 'Split media and text' section.
 *
 * @namespace SplitMediaAndText
 */
import './split-media-and-text.scss'

/**
 * Split media and text section - hide/show functionality for blocks based on
 * handle.
 * @param {HTMLElement} section - Scoped [data-mint-section] <section> element.
 */
export default ({ section = false } = false) => {

  /* Pseudo-state */
  let blocks
  let groupName
  let sectionId
  let blockIdToHandleMap

  /* Get our blocks to match against */
  function getBlocks() {
    blocks = section.querySelectorAll('[data-mint-block-handle]')
  }

  /* Set the group name based on the section's data attribute
   * if we're in shopify theme editor, also populate that data for the block
   * selection handler. In order to match handles to blocks efficiently, build
   * up a map of blockId -> handle rather than getting the handle every time a
   * block is selected.
   * */
  function parseDataAttributes() {
    groupName = section.dataset.mintGroupName

    if (!Shopify?.designMode) {
      return
    }
    sectionId = section.dataset.sectionId

    blockIdToHandleMap = Array.from(blocks).reduce((blockMap, block) => {
      const shopifyEditorData = JSON.parse(block.dataset.shopifyEditorBlock)
      const blockHandle = block.dataset.mintBlockHandle
      return { ...blockMap, [shopifyEditorData.id]: blockHandle }
    }, {})
  }

  /* If we're in the shopify theme editor, show blocks with matching handles
   * when a block is selected
   * */
  function handleBlockSelect(eventData) {
    const matchedKey = Object.keys(blockIdToHandleMap).find((key) =>
      key.includes(eventData.detail.blockId),
    )

    const matchedBlockHandle = matchedKey
      ? blockIdToHandleMap[matchedKey]
      : undefined

    if (eventData.detail.sectionId === sectionId) {
      setVisibleBlocks({ group: groupName, handle: matchedBlockHandle })
    }
  }

  /* Listen for events to hide/show blocks */
  function bindEvents() {
    mint.EventBus.on('ContentGroupSelector:selected', (eventData) => {
      setVisibleBlocks(eventData)
    })

    if (Shopify?.designMode) {
      document.addEventListener('shopify:block:select', handleBlockSelect)
    }
  }

  function isMobile() {
    return window.innerWidth <= 768 ? true : false
  }

  /* When an event is received, show any blocks that match the group and block
   * handles. If none match, hide all
   * */
  function setVisibleBlocks(eventData) {
    if (eventData.group === groupName) {
      const didShow = [...blocks].reduce((didShow, block) => {
        if (block.dataset.mintBlockHandle === eventData.handle) {
          block.classList.add('is-visible')
          return true
        } else {
          block.classList.remove('is-visible')
          return didShow
        }
      }, false)

      if (didShow) {
        // setTimeout added for mobile not scrolling bug
        setTimeout(() => {
          section.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        }, 10)
      }
    } else {
      blocks.forEach(block => {
        block.classList.remove('is-visible')
      })
    }
  }

  /**
   * Initialise component.
   */
  function init() {
    if (section.dataset.mintShowAll) {
      return
    }
    getBlocks()
    parseDataAttributes()
    bindEvents()
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
